import React, { useEffect, useMemo, useState } from "react";
import { CalendarOff, Trash2, CarFront } from "lucide-react";
import { format, parseISO, eachDayOfInterval } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import api, { formatApiErrorDetail } from "../lib/api";

const toISODate = (d) => format(d, "yyyy-MM-dd");

export default function DriverCalendar({ onChanged }) {
  const [items, setItems] = useState([]);
  const [range, setRange] = useState();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/driver/calendar").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const blockedDays = useMemo(() => expand(items.filter((i) => i.kind === "block")), [items]);
  const tripDays = useMemo(() => expand(items.filter((i) => i.kind === "trip")), [items]);

  const addBlock = async () => {
    if (!range?.from) return toast.error("Pick a date or range first");
    setBusy(true);
    try {
      await api.post("/driver/blocks", { start_date: toISODate(range.from), end_date: toISODate(range.to || range.from), reason });
      toast.success("Dates blocked — matching jobs are hidden from your board");
      setRange(undefined); setReason("");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not block dates");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/driver/blocks/${id}`);
      toast.success("Block removed");
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove");
    }
  };

  const blocks = items.filter((i) => i.kind === "block").sort((a, b) => a.start_date.localeCompare(b.start_date));
  const trips = items.filter((i) => i.kind === "trip").sort((a, b) => a.start_date.localeCompare(b.start_date));

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="driver-calendar">
      <div className="lg:col-span-3 rounded-2xl border border-[#E2E8F0] bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Block out dates</p>
          <p className="text-[11px] text-[#64748B] flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#EF4444]/70" /> Blocked</span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#1d7ffd]" /> Accepted trip</span>
          </p>
        </div>
        <div className="mt-3 flex flex-col md:flex-row gap-5">
          <Calendar
            mode="range"
            numberOfMonths={1}
            selected={range}
            onSelect={setRange}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            modifiers={{ blocked: blockedDays, trip: tripDays }}
            modifiersClassNames={{ blocked: "!bg-[#EF4444]/15 !text-[#B91C1C] line-through", trip: "!bg-[#1d7ffd] !text-white" }}
            data-testid="driver-block-calendar"
          />
          <div className="flex-1 space-y-3">
            <p className="text-sm text-[#334155]">
              {range?.from ? (
                <>Blocking <span className="font-bold text-[#0a1f4d]">{format(range.from, "d MMM")}{range.to && range.to > range.from ? ` – ${format(range.to, "d MMM yyyy")}` : ` ${format(range.from, "yyyy")}`}</span></>
              ) : "Select a day or drag a range on the calendar."}
            </p>
            <input className="w-full bg-[#F7F8FA] border border-[#E2E8F0] rounded-xl px-4 py-2.5 text-sm text-[#0a1f4d] outline-none focus:border-[#1d7ffd]" placeholder="Reason (optional) — leave, family, other work…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="driver-block-reason" />
            <button onClick={addBlock} disabled={busy || !range?.from} className="btn-primary text-sm w-full disabled:opacity-50" data-testid="driver-block-add">
              <CalendarOff size={14} /> {busy ? "Saving…" : "Block these dates"}
            </button>
            <p className="text-[11px] text-[#64748B]">Jobs that overlap a blocked date or an accepted trip are hidden from your job board automatically.</p>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Your block-outs</p>
          {blocks.length === 0 && <p className="text-sm text-[#64748B] mt-2">No blocked dates.</p>}
          <ul className="mt-2 divide-y divide-[#EEF2F7]">
            {blocks.map((b) => (
              <li key={b.id} className="py-2.5 flex items-center gap-3" data-testid={`driver-block-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0a1f4d]">{fmtRange(b.start_date, b.end_date)}</p>
                  {b.reason && <p className="text-xs text-[#64748B] truncate">{b.reason}</p>}
                </div>
                <button onClick={() => remove(b.id)} className="text-[#64748B] hover:text-[#B91C1C]" aria-label="Remove block" data-testid={`driver-block-remove-${b.id}`}><Trash2 size={15} /></button>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
          <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Busy with trips</p>
          {trips.length === 0 && <p className="text-sm text-[#64748B] mt-2">No upcoming accepted trips.</p>}
          <ul className="mt-2 divide-y divide-[#EEF2F7]">
            {trips.map((t) => (
              <li key={t.id} className="py-2.5 flex items-center gap-3">
                <CarFront size={15} className="text-[#1d7ffd]" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0a1f4d]">{fmtRange(t.start_date, t.end_date)}</p>
                  <p className="text-xs text-[#64748B] truncate">{t.reason}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function expand(ranges) {
  return ranges.flatMap((r) => eachDayOfInterval({ start: parseISO(r.start_date), end: parseISO(r.end_date) }));
}

function fmtRange(a, b) {
  const s = parseISO(a), e = parseISO(b);
  return a === b ? format(s, "EEE d MMM yyyy") : `${format(s, "d MMM")} – ${format(e, "d MMM yyyy")}`;
}
